function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

function throttle(func, limit) {
  let inThrottle;
  return function (...args) {
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

function handleClickOutside(el, callback) {
  if (!el || typeof el.contains !== "function") return;
  document.addEventListener("click", function (event) {
    if (!el.contains(event.target)) {
      callback();
    }
  });
}

const lerp = (start, end, value) => start * (1.0 - value) + end * value;

const getOffset = (element, scroll = 0) => {
  // Handle case where nothing is passed in
  if (!element) {
    console.error("No element provided to getOffset");
    return null;
  }

  try {
    // Handle case where element is a string (selector)
    if (typeof element === "string") {
      const selectedElement = document.querySelector(element);

      // If querySelector fails (no matching element)
      if (!selectedElement) {
        console.error(`Element not found for selector: ${element}`);
        return null;
      }

      const box = selectedElement.getBoundingClientRect();
      return {
        bottom: box.bottom,
        height: box.height,
        left: box.left,
        top: box.top + scroll,
        width: box.width,
        x: box.x,
        y: box.y,
      };
    }

    // Handle case where element is an actual DOM element
    const box = element.getBoundingClientRect();
    return {
      bottom: box.bottom,
      height: box.height,
      left: box.left,
      top: box.top + scroll,
      width: box.width,
      x: box.x,
      y: box.y,
    };
  } catch (error) {
    console.error("Error in getOffset:", error);
    return null;
  }
};

function each(collection, callback) {
  if (Array.isArray(collection)) {
    for (let i = 0; i < collection.length; i++) {
      callback(collection[i], i, collection);
    }
  } else {
    for (let key in collection) {
      if (Object.prototype.hasOwnProperty.call(collection, key)) {
        callback(collection[key], key, collection);
      }
    }
  }
}

gsap.registerPlugin(ScrollTrigger);

// Don't let ScrollTrigger react to iOS Safari address-bar show/hide resizes,
// which previously triggered scroll-position jumps.
ScrollTrigger.config({ ignoreMobileResize: true });

const toast = document.querySelector(".main-toast");
const toastTl = gsap
  .timeline({ paused: true })
  .to(".main-toast", {
    y: 0,
    opacity: 1,
    ease: "power3.inOut",
    duration: 0.5,
    start: {
      y: "100%",
    },
  })
  .to(".main-toast", {
    delay: 2,
    y: "-100%",
    opacity: 0,
    ease: "power3.inOut",
    duration: 0.5,
  });

const showCart = () => {
  gsap
    .timeline()
    .to(".cart-drawer-wrapper", {
      autoAlpha: 1,
      ease: "linear",
      duration: 0.25,
    })
    .to(
      ".cart-drawer",
      {
        x: 0,
        ease: "expo.out",
        duration: 0.75,
      },
      0
    );
};

const hideCart = () => {
  gsap
    .timeline()
    .to(".cart-drawer", {
      x: "100%",
      ease: "expo.out",
      duration: 0.75,
    })
    .to(
      ".cart-drawer-wrapper",
      {
        autoAlpha: 0,
        ease: "linear",
        duration: 0.25,
      },
      0.2
    );
};

class Nav {
  constructor(container) {
    this.container = container;
    this.nav = document.querySelectorAll(".nav");

    this.menuOpen = document.querySelectorAll(".nav-btn");
    this.menuSpan = document.querySelectorAll(".nav-btn span");
    this.mainLinks = document.querySelectorAll(".nav-links span");
    this.headerLinks = document.querySelectorAll(".header-link");
    this.navLinksToggle = document.querySelectorAll(".nav-links-toggle");

    this.isMenuOpen = false;

    this.reset();
    this.init();
    this.initDropdowns(); // Initialize dropdown animations
    this.initNavLinksToggle(); // Initialize toggle animations
    handleClickOutside(container, () => this.tlNav.reverse());
  }

  reset() { }

  init() {
    this.tlNav = gsap.timeline({
      paused: true,
      onStart: () => {
        this.isMenuOpen = true;
      },
      onReverseComplete: () => {
        this.isMenuOpen = false;
      },
    });

    this.tlNav
      .to(this.nav, {
        clipPath: "inset(0% 0% 0% 0%)",
        ease: "expo.inOut",
        duration: 1,
      })
      .to(
        this.mainLinks,
        {
          y: 0,
          stagger: { each: 0.05, from: "start" },
          ease: "expo.out",
          duration: 0.75,
        },
        0.6
      )
      .to(
        this.menuSpan[0],
        {
          rotation: 45,
          y: "5px",
          ease: "expo.inOut",
          duration: 1,
        },
        0
      )
      .to(
        this.menuSpan[1],
        {
          rotation: -45,
          y: "-5px",
          ease: "expo.inOut",
          duration: 1,
        },
        0
      );

    this.addEventListeners();
  }

  addEventListeners() {
    this.menuOpen.forEach((btn) => {
      btn.addEventListener("click", () =>
        !this.isMenuOpen ? this.tlNav.play() : this.tlNav.reverse()
      );
    });
  }

  initDropdowns() {
    this.headerLinks.forEach((link) => {
      const dropdown = link.querySelector(".header-dropdown");
      const dropdownSvg = link.querySelector(".header-link-svg");
      const sublinks = link.querySelectorAll(".header-sublink");
      const header = document.querySelector(".header");

      if (dropdown) {
        const showDropdown = () => {
          const timeline = gsap.timeline();

          timeline
            .to(dropdown, {
              duration: 0.6,
              clipPath: "inset(0% 0% 0% 0%)",
              ease: "power4.inOut",
            })
            .to(
              sublinks,
              {
                duration: 0.6,
                y: 0,
                opacity: 1,
                stagger: 0.1,
                ease: "power4.inOut",
              },
              0.15
            )
            .to(
              dropdownSvg,
              {
                duration: 0.6,
                rotate: 180,
                ease: "power4.inOut",
              },
              0
            );

          header.classList.add("header--open");
        };

        const hideDropdown = () => {
          const timeline = gsap.timeline();

          timeline
            .to(
              sublinks,
              {
                duration: 0.6,
                y: "10%",
                opacity: 0,
                stagger: 0.1,
                ease: "power4.inOut",
              },
              0
            )
            .to(
              dropdown,
              {
                duration: 0.6,
                clipPath: "inset(0% 0% 100% 0%)",
                ease: "power4.inOut",
              },
              0
            )
            .to(
              dropdownSvg,
              {
                duration: 0.6,
                rotate: 0,
                ease: "power4.inOut",
              },
              0
            );

          header.classList.remove("header--open");
        };

        link.addEventListener("mouseenter", showDropdown);
        link.addEventListener("mouseleave", hideDropdown);
      }
    });
  }

  initNavLinksToggle() {
    this.navLinksToggle.forEach((link) => {
      const dropdown = link.querySelector(".nav-sublinks");
      const dropdownSvg = link.querySelector(".nav-link-svg");
      const sublink = link.querySelectorAll(".nav-sublink-block");

      if (dropdown) {
        let isOpen = false;

        const showDropdown = () => {
          gsap
            .timeline()
            .to(dropdown, {
              duration: 0.6,
              height: "auto",
              ease: "power4.inOut",
            })
            .to(
              dropdownSvg,
              {
                duration: 0.6,
                rotate: 180,
                ease: "power4.inOut",
              },
              0
            );
        };

        const hideDropdown = () => {
          gsap
            .timeline()
            .to(dropdown, {
              duration: 0.6,
              height: "0px",
              ease: "power4.inOut",
            })
            .to(
              dropdownSvg,
              {
                duration: 0.6,
                rotate: 0,
                ease: "power4.inOut",
              },
              0
            );
        };

        link.addEventListener("click", (event) => {
          event.preventDefault();
          if (isOpen) {
            hideDropdown();
          } else {
            showDropdown();
          }
          isOpen = !isOpen;
        });

        sublink.forEach(element => {
          element.addEventListener("click", (event) => {
            event.stopPropagation()
          });
        });
      }
    });
  }
}

let cart;
let cartTimeout;
class CartManager {
  constructor() {
    this.cartCount = document.querySelectorAll("#cart-count, .cart-btn span");
    this.addToCartForms = document.querySelectorAll('form[action="/cart/add"]');

    this.initEventListeners();
  }

  initEventListeners() {
    document.addEventListener("click", this.handleCartClick.bind(this));
    this.addToCartForms.forEach((form) => {
      // Submit catches both button click + keyboard Enter; using submit
      // (vs click on form) means we never miss the inner-span click case
      // where event.target was the <span> nested inside the .add-to-cart
      // button and `target.matches('.add-to-cart')` returned false.
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.handleAddToCart(form);
      });
    });
  }

  async handleAddToCart(form) {
    const idInput = form.querySelector('input[name="id"]');
    if (!idInput || !idInput.value) {
      toast.textContent = "Missing variant id.";
      toastTl.restart();
      return;
    }
    const sellingPlanInput = form.querySelector(
      ':scope > input[name="selling_plan"], :scope > select[name="selling_plan"]'
    );

    const requestBody = {
      id: idInput.value,
      quantity: 1,
    };
    if (sellingPlanInput && sellingPlanInput.value) {
      requestBody.selling_plan = sellingPlanInput.value;
    }

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        // Surface Shopify's actual error message instead of a generic string
        let detail = `${response.status}`;
        try {
          const data = await response.json();
          detail = data.description || data.message || JSON.stringify(data);
        } catch (_) { /* noop */ }
        throw new Error(detail);
      }

      // Klaviyo Add to Cart tracking
      window._learnq = window._learnq || [];
      window._learnq.push([
        "track",
        "Added to Cart",
        { ProductID: requestBody.id, Quantity: requestBody.quantity },
      ]);

      const cartResponse = await fetch("/cart.js");
      if (!cartResponse.ok) throw new Error("Failed to fetch cart data.");
      const cart = await cartResponse.json();
      await this.updateCartTotal(cart.item_count);
      showCart();
    } catch (error) {
      console.error("Error adding item to cart:", error);
      toast.textContent = `Couldn't add to cart: ${error.message}`;
      toastTl.restart();
    }
  }

  getVariantFromURL() {
    // Extract the variant ID from the URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("variant"); // Returns the variant ID or null if not found
  }


  async handleCartClick(event) {
    if (event.target.matches(".cart-remove")) {
      await this.handleRemoveItem(event);
    }

    if (event.target.closest(".cart-btn")) {
      showCart();
    }
    if (
      event.target.closest(".cart-close") ||
      event.target.matches(".cart-drawer-wrapper")
    ) {
      hideCart();
    }
    if (event.target.closest(".cart-drawer")) {
      event.stopPropagation();
    }
    if (
      event.target.closest(".decrease-cart-quantity, .increase-cart-quantity")
    ) {
      await this.handleQuantityChange(event);
    }
  }


  async handleQuantityChange(event) {
    try {
      let cartItem = event.target.closest(".cart-item");
      let quantityInput = cartItem.querySelector(".cart-quantity-input");
      let currentQuantity = parseInt(quantityInput.value, 10);
      let newQuantity = event.target.classList.contains("decrease-cart-quantity")
        ? Math.max(currentQuantity - 1, 0)
        : currentQuantity + 1;

      quantityInput.value = newQuantity;

      clearTimeout(cartTimeout);
      cartTimeout = setTimeout(async () => {

        let variantId = cartItem.dataset.variant_id;
        let key = cartItem.dataset.key;

        // Create combined ID if it's a subscription item
        let combinedId = key || variantId;

        // Update the cart with the combined ID
        await this.updateCartQuantity(combinedId, newQuantity);
      }, 300)

    } catch (error) {
      console.error("Error handling quantity change:", error);
    }
  }

  async handleRemoveItem(event) {
    try {
      let cartItem = event.target.closest(".cart-item");
      let variantId = cartItem.dataset.variant_id;
      let key = cartItem.dataset.key;

      await this.updateCartQuantity(key || variantId, 0);
    } catch (error) {
      console.error("Error removing cart item:", error);
    }
  }

  async updateCartQuantity(combinedId, newQuantity) {
    try {
      // Create the request body for the cart update
      let bodyData = {
        id: combinedId,
        quantity: newQuantity,
      };

      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bodyData),
      });

      const cart = await response.json();

      if (cart.status === 422) {
        toast.textContent = cart.message;
        toastTl.restart();
      } else {
        toast.textContent = "Cart updated successfully";
        toastTl.restart();
      }

      await this.updateCartTotal(cart.item_count);
    } catch (error) {
      console.error("Error updating cart:", error);
    }
  }

  updateCartCount(count) {
    if (this.cartCount.length > 0) {
      this.cartCount.forEach((element) => {
        element.textContent = count;
      });
    }
  }

  async updateCartTotal(count) {
    this.updateCartCount(count);
    try {
      // Fetch the cart content (this will work for both modal and page)
      const response = await fetch("/cart");
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, "text/html");

      // Detect if you're on the cart page
      const isCartPage = document.querySelector(".cart-page") !== null;

      if (isCartPage) {
        // Update cart page content
        const cartContentWrappers = document.querySelectorAll(".cart-page");
        cartContentWrappers.forEach((wrapper) => {
          const newContent = html.querySelector(".cart-page");
          if (newContent) {
            wrapper.innerHTML = newContent.innerHTML;
          }
        });

        const cartModalWrappers = document.querySelectorAll(".cart-drawer");
        cartModalWrappers.forEach((wrapper) => {
          const newModalContent = html.querySelector(".cart-drawer");
          if (newModalContent) {
            wrapper.innerHTML = newModalContent.innerHTML;

            if (count > 0) {
              wrapper?.classList.remove("cart-drawer--empty");
            } else {
              wrapper?.classList.add("cart-drawer--empty");
            }
          }
        });
      } else {
        // Update cart modal content if you're not on the cart page
        const cartModalWrappers = document.querySelectorAll(".cart-drawer");
        cartModalWrappers.forEach((wrapper) => {
          const newModalContent = html.querySelector(".cart-drawer");
          if (newModalContent) {
            wrapper.innerHTML = newModalContent.innerHTML;

            if (count > 0) {
              wrapper?.classList.remove("cart-drawer--empty");
            } else {
              wrapper?.classList.add("cart-drawer--empty");
            }
          }
        });
      }
    } catch (error) {
      console.error("Error updating cart content:", error);
    }
  }

}

const initialiseLenis = () => {
  const lenis = new Lenis({
    duration: 1,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    orientation: "vertical",
    gestureOrientation: "vertical",
    smoothWheel: true,
    wheelMultiplier: 0.9,
    touchMultiplier: 1,
    // Lenis >=1.1 replaced `smoothTouch` with `syncTouch`. Leaving it false keeps
    // native touch scrolling (same behaviour as before) — Lenis does not hijack
    // touch momentum on iOS, which is what caused the scroll-to-top.
    syncTouch: false,
    infinite: false,
  });
  window.globalLenis = lenis;

  // Keep Lenis and ScrollTrigger on a single, shared scroll loop. Previously they
  // ran independent rAF loops; on iOS that desync let ScrollTrigger reset scroll
  // to the top when native momentum settled. Driving Lenis from gsap.ticker and
  // updating ScrollTrigger on every Lenis scroll keeps them coordinated.
  lenis.on("scroll", ScrollTrigger.update);
  gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
  });
  gsap.ticker.lagSmoothing(0);

  lenis.scrollTo(0, { immediate: true });
  if (window.history.scrollRestoration) {
    window.history.scrollRestoration = "manual";
  }
};

class Parallax {
  constructor({
    element,
    speed = 1,
    id = "parallax",
    scale = 1.1,
    position,
    rotation = 0,
  }) {
    this.element = element;
    this.id = id;
    this.windowWidth = window.innerWidth;

    this.speed = this.element.dataset.speed || speed;
    this.scale = this.element.dataset.scale || scale;
    this.rotation = this.element.dataset.rotation || rotation;
    this.position = this.element.dataset.position || position;

    this.timeline = null;
    this.trigger = element;
    this.target = element.querySelector("[data-target]");

    this.init();
  }

  init() {
    const y = this.windowWidth * this.speed * 0.125;
    const mm = gsap.matchMedia();

    this.timeline = gsap
      .timeline({
        scrollTrigger: {
          id: this.id,
          trigger: this.position === "top" ? document.body : this.trigger,
          scrub: 0.5,
          start: this.position === "top" ? "top top" : "top bottom",
          end: this.position === "top" ? "+=100%" : "bottom top",
        },
      })
      .fromTo(
        this.target,
        { y: this.position === "top" ? 0 : -y, scale: this.scale },
        { y: y, ease: "none", scale: 1 }
      )
      .to(this.element, { rotate: this.rotation }, 0);

    mm.add(
      {
        reduceMotion: "(prefers-reduced-motion: reduce)",
      },
      (context) => {
        const { reduceMotion } = context.conditions;

        if (reduceMotion) {
          this.timeline?.from(this.target, { y: 0 });
          this.timeline?.kill();
        }
      }
    );
  }
}

class TextReveal {
  constructor(el) {
    this.el = el;
    this.split = null;
    this.reset();

    this.resize = this.resize.bind(this);
    window.addEventListener("resize", this.resize);
  }

  reset() {
    const elements = gsap.utils.toArray(this.el);
    elements.forEach((element) => {
      this.split = new SplitType(element, {
        types: "lines,words",
        absolute: !!element.dataset.position,
      });
      gsap
        .timeline()
        .set(this.split.lines, {
          overflowY: "hidden",
          marginBottom: "-15px",
          paddingBottom: "10px",
          marginTop: "-10px",
          paddingTop: "10px",
        })
        .set(this.split.words, {
          y: "110%",
          opacity: 0,
          willChange: "transform",
        });
    });
  }

  show() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            const lines = entry.target.querySelectorAll(".line");
            gsap.utils.toArray(lines).forEach((element, idx) => {
              const words = element.querySelectorAll(".word");
              gsap.to(words, {
                y: 0,
                opacity: 1,
                ease: "power3.inOut",
                delay: idx * 0.07,
                duration: 1,
                startAt: {
                  y: "100%",
                },
              });
            });
          }
        });
      },
      { threshold: 0.5 }
    );

    gsap.utils.toArray(this.el).forEach((element) => {
      observer.observe(element);
    });
  }

  resize() {
    this.reset();
    this.show();
  }
}

class CharReveal {
  constructor(el) {
    this.el = el;
    this.split = null;
    this.reset();
  }

  reset() {
    const elements = gsap.utils.toArray(this.el);
    elements.forEach((element) => {
      this.split = new SplitType(element, {
        types: "lines,chars",
      });
      gsap
        .timeline()
        .set(this.split.lines, {
          overflowY: "hidden",
        })
        .set(this.split.chars, {
          y: "110%",
          willChange: "transform",
        });
    });
  }

  show() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            const lines = entry.target.querySelectorAll(".line");
            gsap.utils.toArray(lines).forEach((element) => {
              const chars = element.querySelectorAll(".char");
              gsap.to(chars, {
                ease: "expo.inOut",
                y: 0,
                duration: 1.25,
                stagger: 0.025,
                startAt: {
                  y: "100%",
                },
              });
            });
          }
        });
      },
      { threshold: 0.75 }
    );

    gsap.utils.toArray(this.el).forEach((element) => {
      observer.observe(element);
    });
  }

  resize() {
    this.reset();
    this.show();
  }
}

class InfiniteX {
  constructor() {
    this.speed = 1;

    this.el = null;
    this.elements = [];
    this.scroll = {
      ease: 0.05,
      position: 0,
      current: 0,
      target: 0,
      last: 0,
    };
    this.isEnabled = true;
    this.isDown = false;
    this.widthTotal = null;
    this.start = null;
    this.velocity = this.speed;
    this.direction = "down";
    this.isInverted = false;

    // Bind methods to the instance
    this.onResize = this.onResize.bind(this);
    this.onTouchDown = this.onTouchDown.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchUp = this.onTouchUp.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.update = this.update.bind(this);
  }

  init(el) {
    this.el = el;

    // Check for the inverted flag
    const isInverted = this.el.dataset.inverted === "true";
    this.isInverted = isInverted; // Set flag based on data attribute

    this.setWidthTotal();
    this.attachEventListeners();
  }

  setWidthTotal() {
    const elements = this.el.querySelectorAll(
      '[data-animation="draggable-child"]'
    );
    this.elements = Array.from(elements);
    let totalWidth = 0;
    this.elements.forEach((element) => {
      const styles = getComputedStyle(element);
      const margin =
        parseFloat(styles.marginLeft) + parseFloat(styles.marginRight);
      const offset = getOffset(element);
      element.extra = 0;
      element.width = offset.width + margin;
      element.offset = offset.left;
      element.position = 0;
      totalWidth += offset.width + margin;
    });
    this.widthTotal = totalWidth;
  }

  transform(element, x) {
    gsap.set(element, {
      x: Math.floor(x),
    });
  }

  onTouchDown(event) {
    if (!this.isEnabled) return;
    this.isDown = true;
    this.el.style.cursor = "grabbing";
    this.scroll.position = this.scroll.current;
    this.start = "touches" in event ? event.touches[0].clientX : event.clientX;
  }

  onTouchMove(event) {
    if (!this.isDown || !this.isEnabled) return;
    const x = "touches" in event ? event.touches[0].clientX : event.clientX;
    const distance = (this.start - x) * 2;
    this.scroll.target = this.scroll.position + distance;
  }

  onTouchUp() {
    if (!this.isEnabled) return;
    this.isDown = false;
    this.el.style.cursor = "grab";
  }

  onWheel(event) {
    if (!this.isEnabled) return;
    const normalized = normalizeWheel(event);
    const invertedFactor = this.isInverted ? -1 : 1;
    this.scroll.target +=
      (normalized.pixelY + normalized.pixelX) * 0.5 * invertedFactor;
  }

  onResize() {
    each(this.elements, (element) => {
      this.transform(element, 0);
    });

    this.scroll = {
      ease: 0.05,
      position: 0,
      current: 0,
      target: 0,
      last: 0,
    };
    this.widthTotal = null;
    this.start = null;
    this.setWidthTotal();
  }

  update() {
    if (!this.isEnabled) return;

    this.scroll.target += this.velocity;

    // Smooth scroll using lerp
    this.scroll.current = lerp(
      this.scroll.current,
      this.scroll.target,
      this.scroll.ease
    );

    // Determine the actual direction of scrolling (up or down)
    if (this.scroll.current < this.scroll.last) {
      this.direction = "down";
      this.velocity = -this.speed;
    } else {
      this.direction = "up";
      this.velocity = this.speed;
    }

    // Update positions of elements based on scroll position
    each(this.elements, (element) => {
      element.position = -this.scroll.current - element.extra;

      const offset = element.position + element.offset + element.width;

      element.isBefore = offset < 0;
      element.isAfter = offset > this.widthTotal;

      // Loop elements when they move out of bounds (adjust based on direction)
      if (this.direction === "up" && element.isBefore) {
        element.extra -= this.widthTotal;
        element.isBefore = false;
        element.isAfter = false;
      }

      if (this.direction === "down" && element.isAfter) {
        element.extra += this.widthTotal;
        element.isBefore = false;
        element.isAfter = false;
      }

      // Apply the transform to position the element
      this.transform(element, element.position);
    });

    // Save the last scroll position for the next frame
    this.scroll.last = this.scroll.current;

    // Continue the update loop
    requestAnimationFrame(this.update);
  }

  attachEventListeners() {
    window.addEventListener("resize", this.onResize);
    window.addEventListener("wheel", this.onWheel);
    this.el.addEventListener("mousedown", this.onTouchDown);
    this.el.addEventListener("mousemove", this.onTouchMove);
    this.el.addEventListener("mouseup", this.onTouchUp);
    this.el.addEventListener("touchstart", this.onTouchDown);
    this.el.addEventListener("touchmove", this.onTouchMove);
    this.el.addEventListener("touchend", this.onTouchUp);

    this.onResize();
    this.update();
  }

  destroy() {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("wheel", this.onWheel);
    this.el.removeEventListener("mousedown", this.onTouchDown);
    this.el.removeEventListener("mousemove", this.onTouchMove);
    this.el.removeEventListener("mouseup", this.onTouchUp);
    this.el.removeEventListener("touchstart", this.onTouchDown);
    this.el.removeEventListener("touchmove", this.onTouchMove);
    this.el.removeEventListener("touchend", this.onTouchUp);
  }
}

class ObserveReveal {
  constructor(el) {
    this.el = el;
  }

  reset() {
    gsap.utils.toArray(this.el).forEach((element) => {
      gsap.set(element, {
        autoAlpha: 0,
        willChange: "opacity",
      });
    });
  }

  show() {
    return new Promise((resolve) => {
      let revealedCount = 0;

      const observer = new window.IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              observer.unobserve(entry.target);
              gsap.to(entry.target, {
                autoAlpha: 1,
                duration: 1,
                onComplete: () => {
                  revealedCount++;
                  if (revealedCount === entries.length) {
                    resolve();
                  }
                },
              });
            }
          });
        },
        { threshold: 0.2 }
      );

      gsap.utils.toArray(this.el).forEach((element) => {
        observer.observe(element);
      });
    });
  }

  resize() {
    this.reset();
    return this.show();
  }
}

class CTA {
  constructor(el) {
    this.el = el;
    this.intialise();
  }

  intialise() {
    const originalContent = `<span data-cta-original>${this.el.innerHTML}</span>`;
    const duplicate = `<span data-cta-duplicate>${this.el.innerHTML}</span>`;

    this.el.innerHTML = `<div data-cta-wrapper>${originalContent + duplicate}</div`;
    gsap.set("[data-cta-wrapper]", {
      position: "relative",
      pointerEvents: "none",
      overflow: "hidden",
    });
    gsap.set(["[data-cta-original]", "[data-cta-duplicate]"], {
      display: "inline-flex",
      gap: "max(1.2rem, 10px)",
    });
    gsap.set("[data-cta-duplicate]", {
      position: "absolute",
      left: 0,
      right: 0,
      top: "100%",
    });

    // Use arrow functions to keep `this` context
    this.el.addEventListener("mouseover", () => {
      const wrapper = this.el.querySelector("[data-cta-wrapper]");
      gsap.to(wrapper.querySelectorAll("span"), {
        y: "-100%",
        duration: 0.5,
        ease: "expo.out",
      });
    });

    this.el.addEventListener("mouseout", () => {
      const wrapper = this.el.querySelector("[data-cta-wrapper]");
      gsap.to(wrapper.querySelectorAll("span"), {
        y: "0",
        duration: 0.5,
        ease: "expo.out",
      });
    });
  }
}

class Scrollable {
  constructor({ element, child }) {
    this.element = element;
    this.content = child;

    this.lenis = null;
    this.start();
  }

  start() {
    this.lenis = new Lenis({
      wrapper: this.element,
      content: this.content,
      duration: 1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      // Lenis >=1.1 renamed these options: direction->orientation,
      // gestureDirection->gestureOrientation, mouseMultiplier->wheelMultiplier,
      // smooth->smoothWheel, smoothTouch->syncTouch. Mapped 1:1 to preserve behaviour.
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 1.1,
      syncTouch: false,
    });

    this.raf();
  }

  raf() {
    this.lenis.start();
    const raf = (time) => {
      this.lenis.raf(time);
      requestAnimationFrame(raf);
    };

    requestAnimationFrame(raf);
  }
}

function initDraggableList(listSelector, wrapperSelector, multiplier = 1000) {
  let draggableInstance = null;

  const setupDraggable = () => {
    const ingredientsList = document.querySelector(listSelector);
    const wrapper = document.querySelector(wrapperSelector);
    if (!ingredientsList || !wrapper) return;

    const prevButton = wrapper.querySelector(".carousel-navigation--prev") || wrapper.closest('.shopify-section').querySelector(".carousel-navigation--prev");
    const nextButton = wrapper.querySelector(".carousel-navigation--next") || wrapper.closest('.shopify-section').querySelector(".carousel-navigation--next");

    const getBounds = () => {
      const wrapperBounds = wrapper.getBoundingClientRect();
      const listBounds = ingredientsList.getBoundingClientRect();
      return {
        min: Math.min(0, wrapperBounds.width - listBounds.width),
        max: 0,
      };
    };

    const updateButtonStates = () => {
      const bounds = getBounds();
      const currentX = gsap.getProperty(ingredientsList, "x");

      if (prevButton) prevButton.disabled = parseInt(currentX) >= parseInt(bounds.max);
      if (nextButton) nextButton.disabled = parseInt(currentX) <= parseInt(bounds.min);
    };

    gsap.registerPlugin(Draggable);

    if (draggableInstance) {
      draggableInstance[0].kill();
    }

    let lastX = 0;
    let lastTime = Date.now();
    let velocity = 0;

    draggableInstance = Draggable.create(ingredientsList, {
      type: "x",
      bounds: wrapper,
      edgeResistance: 0.85,
      onDragStart: function () {
        gsap.killTweensOf(ingredientsList);
      },
      onDrag: function () {
        const currentTime = Date.now();
        const deltaX = this.x - lastX;
        const deltaTime = currentTime - lastTime;

        velocity = deltaX / deltaTime;

        lastX = this.x;
        lastTime = currentTime;

        updateButtonStates();
      },
      onDragEnd: function () {
        const bounds = getBounds();

        gsap.to(ingredientsList, {
          x: `+=${velocity * multiplier}`,
          duration: 1,
          ease: "power3.out",
          onUpdate: function () {
            const currentX = gsap.getProperty(ingredientsList, "x");
            if (currentX > bounds.max) {
              gsap.set(ingredientsList, { x: bounds.max });
            } else if (currentX < bounds.min) {
              gsap.set(ingredientsList, { x: bounds.min });
            }
            updateButtonStates();
          },
          onComplete: function () {
            const currentX = gsap.getProperty(ingredientsList, "x");
            if (currentX > bounds.max) {
              gsap.to(ingredientsList, { x: bounds.max, duration: 0.2 });
            } else if (currentX < bounds.min) {
              gsap.to(ingredientsList, { x: bounds.min, duration: 0.2 });
            }
            updateButtonStates();
          },
        });
      },
    });

    const scrollBy = (distance) => {
      const bounds = getBounds();

      gsap.to(ingredientsList, {
        x: `+=${distance}`,
        duration: 1,
        ease: "power3.out",
        onUpdate: function () {
          const currentX = gsap.getProperty(ingredientsList, "x");
          if (currentX > bounds.max) {
            gsap.set(ingredientsList, { x: bounds.max });
          } else if (currentX < bounds.min) {
            gsap.set(ingredientsList, { x: bounds.min });
          }
          updateButtonStates();
        },
        onComplete: function () {
          const currentX = gsap.getProperty(ingredientsList, "x");
          if (currentX > bounds.max) {
            gsap.to(ingredientsList, { x: bounds.max, duration: 0.2 });
          } else if (currentX < bounds.min) {
            gsap.to(ingredientsList, { x: bounds.min, duration: 0.2 });
          }
          updateButtonStates();
        },
      });
    };

    if (nextButton) {
      nextButton.addEventListener("click", () => {
        const moveBy = -window.innerWidth * 0.5; // 50vw
        scrollBy(moveBy);
      });
    }

    if (prevButton) {
      prevButton.addEventListener("click", () => {
        const moveBy = window.innerWidth * 0.5; // 50vw
        scrollBy(moveBy);
      });
    }

    updateButtonStates();
  };

  // Initialize Draggable
  setupDraggable();

  // Observe for resizes
  const resizeObserver = new ResizeObserver(() => {
    setupDraggable();
  });

  const wrapper = document.querySelector(wrapperSelector);
  if (wrapper) resizeObserver.observe(wrapper);
}



document.addEventListener("DOMContentLoaded", () => {
  if (!document.body.classList.contains("is-listicle")) {
    initialiseLenis();
  }

  new Nav(document.querySelector(".header-wrapper"));
  cart = new CartManager();

  document.querySelectorAll("[data-parallax]").forEach((element) => {
    new Parallax({ element });
  });
  document.querySelectorAll("[data-cta]").forEach((element) => {
    new CTA(element);
  });

  document.querySelectorAll("[data-paragraph]").forEach((element) => {
    setTimeout(() => {
      const textReveal = new TextReveal(element);
      textReveal.resize();
    }, 1000);
  });

  document.querySelectorAll("[data-chars]").forEach((element) => {
    const charReveal = new CharReveal(element);
    charReveal.resize();
  });

  document
    .querySelectorAll('[data-animation="draggable"]')
    .forEach((element) => {
      const infiniteDrag = new InfiniteX();
      infiniteDrag.init(element);
    });

  document.querySelectorAll("[data-reveal]").forEach((element) => {
    const observeReveal = new ObserveReveal(element);
    observeReveal.resize();
  });

  document.querySelectorAll("[data-scrollable]").forEach((element) => {
    const child = element.querySelector("[data-scrollable-child]");
    new Scrollable({ element, child });
  });

  // Set header height as CSS variable
  function updateHeaderHeight() {
    const header = document.querySelector("#shopify-section-header");
    if (header) {
      const height = header.offsetHeight;
      document.documentElement.style.setProperty('--hd-height', `${height}px`);
    }
  }

  // Initial update
  updateHeaderHeight();

  // Update on resize
  const debouncedUpdateHeaderHeight = debounce(updateHeaderHeight, 100);
  window.addEventListener('resize', debouncedUpdateHeaderHeight);
});


